import { v4 as uuidv4 } from 'uuid'
import { create } from 'zustand'

import { SnackbarProps } from '@/components/Snackbar'

export type Snackbar = Omit<SnackbarProps, 'onClose' | 'onHover'> & {
  id: string
  duration: number
  timeout?: ReturnType<typeof setTimeout>
  /** When the running timeout was started, to work out what is left of it. */
  startedAt?: number
  remaining: number
  isPaused: boolean
}

type SnackbarState = {
  snackbars: Snackbar[]
  addSnackbar: (
    snackbar: Omit<
      Snackbar,
      'id' | 'duration' | 'timeout' | 'startedAt' | 'remaining' | 'isPaused'
    > & { duration?: number },
  ) => void
  removeSnackbar: (id: string) => void
  setHovered: (id: string, hovered: boolean) => void
}

const DEFAULT_DURATION = 5000

export const useSnackbarStore = create<SnackbarState>((set, get) => ({
  snackbars: [],

  addSnackbar: ({ duration = DEFAULT_DURATION, ...snackbar }) => {
    const id = uuidv4()

    set(state => ({
      snackbars: [
        {
          ...snackbar,
          id,
          duration,
          timeout: setTimeout(() => get().removeSnackbar(id), duration),
          startedAt: Date.now(),
          remaining: duration,
          isPaused: false,
        },
        ...state.snackbars,
      ],
    }))
  },

  removeSnackbar: id => {
    set(state => {
      const snackbar = state.snackbars.find(s => s.id === id)
      if (snackbar?.timeout) clearTimeout(snackbar.timeout)
      return { snackbars: state.snackbars.filter(s => s.id !== id) }
    })
  },

  // Hovering holds the message open. What is left of the countdown is carried
  // across the pause, so a second hover cannot keep resetting it to full.
  setHovered: (id, hovered) => {
    set(state => ({
      snackbars: state.snackbars.map(s => {
        if (s.id !== id || s.isPaused === hovered) return s

        if (hovered) {
          if (s.timeout) clearTimeout(s.timeout)
          const elapsed = s.startedAt ? Date.now() - s.startedAt : 0
          return {
            ...s,
            isPaused: true,
            timeout: undefined,
            startedAt: undefined,
            remaining: Math.max(0, s.remaining - elapsed),
          }
        }

        return {
          ...s,
          isPaused: false,
          startedAt: Date.now(),
          timeout: setTimeout(() => get().removeSnackbar(id), s.remaining),
        }
      }),
    }))
  },
}))
